import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../common/Header";
import Footer from "../common/Footer";
import PageWrapper from "../common/PageWrapper";

// Import des composants pour véhicules d'occasion
import DisplayVehiculeOccasion from "./Vehicule/VehiculeOccasion/DisplayVehiculeOccasion";
import DisplayTitulairesOccasion from "./Titulaires/VehiculeOccasion/DisplayTitulairesOccasion";
import DisplayLocationVehiculeOccasion from "./Location/VehiculeOccasion/DisplayLocationVehiculeOccasion";
import DisplaySignatureOccasion from "./Signature/VehiculeOccasion/DisplaySignatureOccasion";

// Import des composants pour véhicules neufs
import DisplayVehiculeNeuf from "./Vehicule/VehiculeNeuf/DisplayVehiculeNeuf";
import DisplayTitulairesNeuf from "./Titulaires/VehiculeNeuf/DisplayTitulairesNeuf";
import DisplayLocationVehiculeNeuf from "./Location/VehiculeNeuf/DisplayLocationVehiculeNeuf";
import DisplaySignatureNeuf from "./Signature/VehiculeNeuf/DisplaySignatureNeuf";

import "./VehiculeForm.css";
import "./VehiculeViewPage.css";

const VehiculeViewPage = ({ menuState }) => {
    const { isMenuOpen, setIsMenuOpen, onMenuToggle } = menuState || {};
    const { id } = useParams();
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002/api";

    const [vehiculeData, setVehiculeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);

    useEffect(() => {
        const fetchVehiculeData = async () => {
            if (!id) return;
            
            setLoading(true);
            setError(null);
            
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get(`${API_URL}/vehicules/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                
                if (response.data) {
                    // Convertir les valeurs numériques des boutons radio en chaînes de caractères
                    const processedData = { ...response.data };
                    
                    // Liste des champs radio à convertir en chaînes de caractères
                    const radioFields = [
                        // Étape 1
                        'veh_33', 'veh_34', 'occ_veh_32', 'veh_0', 'veh_5', 'ccv_1',
                        // Étape 2
                        'tit_1', 'tit_2', 'cotit_0', 'cotit_2', 
                        'occ_app_1', 'occ_app_2', 'occ_app_12', 
                        'occ_tit_1', 'occ_tit_2', 'occ_cotit_0', 'occ_cotit_2',
                        // Étape 3
                        'loc_0', 'loc_1', 'loc_5', 'loc_17', 'loc_18'
                    ];
                    
                    radioFields.forEach(field => {
                        if (processedData[field] !== undefined && processedData[field] !== null) {
                            processedData[field] = processedData[field].toString();
                        }
                    });

                    console.log("Données reçues du backend :", response.data);

                    setVehiculeData(processedData);
                }
            } catch (error) {
                console.error("Error fetching vehicle data:", error);
                setError("Erreur lors de la récupération des données du véhicule. Veuillez réessayer.");
            } finally {
                setLoading(false);
            }
        };

        fetchVehiculeData();
    }, [id, API_URL]);

    const handleBackClick = () => {
        navigate(-1);
    };

    const handleNextClick = () => {
        if (currentStep < 4) {
            setCurrentStep(currentStep + 1);
        }
    };

    const getVehiculeType = () => {
        if (!vehiculeData) return null;
        return vehiculeData.etat === "VN" ? "Neuf" : "Occasion";
    };

    const isVehiculeNeuf = vehiculeData?.etat === "VN";
    
    if (loading) {
        return (
            <div className="vehicle-form-container">
                <Header
                    isMenuOpen={isMenuOpen}
                    setIsMenuOpen={setIsMenuOpen}
                    onMenuToggle={onMenuToggle}
                />
                <PageWrapper isMenuOpen={isMenuOpen}>
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Chargement des données du véhicule...</p>
                    </div>
                </PageWrapper>
                <Footer />
            </div>
        );
    }
    
    if (error || !vehiculeData) {
        return (
            <div className="vehicle-form-container">
                <Header
                    isMenuOpen={isMenuOpen}
                    setIsMenuOpen={setIsMenuOpen}
                    onMenuToggle={onMenuToggle}
                />
                <PageWrapper isMenuOpen={isMenuOpen}>
                    <div className="error-container">
                        <p>{error || "Aucune donnée disponible pour ce véhicule."}</p>
                        <button className="back-button" onClick={handleBackClick}>
                            Retour
                        </button>
                    </div>
                </PageWrapper>
                <Footer />
            </div>
        );
    }

    return (
        <div className="vehicle-form-container">
            <Header
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
                onMenuToggle={onMenuToggle}
            />
            <PageWrapper isMenuOpen={isMenuOpen}>
                <div className="step-container view-only-container">
                    <div className="view-only-banner">
                        <span className="eye-icon">👁️</span>
                        <span>MODE CONSULTATION</span>
                    </div>

                    <div className="form-header">
                        <div className="form-group">
                            <label htmlFor="numero_dossier">DOSSIER N°</label>
                            <input
                                type="text"
                                id="numero_dossier"
                                value={vehiculeData.numero_dossier || ''}
                                readOnly
                                className="read-only-input"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="statut">STATUT:</label>
                            <input
                                type="text"
                                id="statut"
                                value={vehiculeData.statut || ''}
                                readOnly
                                className="read-only-input"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="vendeur">VENDEUR:</label>
                            <input
                                type="text"
                                id="vendeur"
                                value={vehiculeData.vendeur_nom || ''}
                                readOnly
                                className="read-only-input"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="created_at">CRÉÉ LE:</label>
                            <input
                                type="text"
                                id="created_at"
                                value={vehiculeData.created_at ? new Date(vehiculeData.created_at).toLocaleString('fr-FR') : ''}
                                readOnly
                                className="read-only-input"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="updated_at">MODIFIÉ LE:</label>
                            <input
                                type="text"
                                id="updated_at"
                                value={vehiculeData.updated_at ? new Date(vehiculeData.updated_at).toLocaleString('fr-FR') : ''}
                                readOnly
                                className="read-only-input"
                            />
                        </div>
                    </div>

                    <div className="section">
                        <h3>TYPE DE VÉHICULE</h3>
                        <div className="radio-group">
                            <label>VÉHICULE:</label>
                            <div className='radio-group-child'>
                                <div>
                                    <input
                                        type="radio"
                                        id="neuf"
                                        name="vehiculeType"
                                        checked={getVehiculeType() === 'Neuf'}
                                        readOnly
                                        disabled
                                    />
                                    <label htmlFor="neuf">NEUF</label>
                                </div>
                                <div>
                                    <input
                                        type="radio"
                                        id="occasion"
                                        name="vehiculeType"
                                        checked={getVehiculeType() === 'Occasion'}
                                        readOnly
                                        disabled
                                    />
                                    <label htmlFor="occasion">OCCASION</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="progress-indicator">
                        <div 
                            className={currentStep === 1 ? "progress-step active" : "progress-step"}
                            onClick={() => setCurrentStep(1)}
                        >
                            <div className="step-content">VÉHICULE</div>
                                </div>
                        <div 
                            className={currentStep === 2 ? "progress-step active" : "progress-step"}
                            onClick={() => setCurrentStep(2)}
                        >
                            <div className="step-content">TITULAIRES</div>
                            </div>
                        <div 
                            className={currentStep === 3 ? "progress-step active" : "progress-step"}
                            onClick={() => setCurrentStep(3)}
                        >
                            <div className="step-content">LOCATION</div>
                                    </div>
                        <div 
                            className={currentStep === 4 ? "progress-step active" : "progress-step"}
                            onClick={() => setCurrentStep(4)}
                        >
                            <div className="step-content">SIGNATURES</div>
                                    </div>
                                    </div>
                                    
                    {currentStep === 1 && (
                        isVehiculeNeuf ? (
                            <DisplayVehiculeNeuf vehiculeData={vehiculeData} readOnly={true} />
                        ) : (
                            <DisplayVehiculeOccasion vehiculeData={vehiculeData} readOnly={true} />
                        )
                    )}

                    {currentStep === 2 && (
                        isVehiculeNeuf ? (
                            <DisplayTitulairesNeuf vehiculeData={vehiculeData} readOnly={true} />
                        ) : (
                            <DisplayTitulairesOccasion vehiculeData={vehiculeData} readOnly={true} />
                        )
                    )}

                    {currentStep === 3 && (
                        isVehiculeNeuf ? (
                            <DisplayLocationVehiculeNeuf vehiculeData={vehiculeData} readOnly={true} />
                        ) : (
                            <DisplayLocationVehiculeOccasion vehiculeData={vehiculeData} readOnly={true} />
                        )
                    )}

                    {currentStep === 4 && (
                        isVehiculeNeuf ? (
                            <DisplaySignatureNeuf vehiculeData={vehiculeData} readOnly={true} />
                        ) : (
                            <DisplaySignatureOccasion vehiculeData={vehiculeData} readOnly={true} />
                        )
                    )}

                    <div className="button-container">
                        <button className="prev-button" onClick={handleBackClick}>
                            RETOUR
                        </button>
                        {currentStep < 4 && (
                            <button className="next-button" onClick={handleNextClick}>
                                SUIVANT
                            </button>
                        )}
                    </div>
                </div>
            </PageWrapper>
            <Footer />
        </div>
    );
};

VehiculeViewPage.propTypes = {
    menuState: PropTypes.shape({
        isMenuOpen: PropTypes.bool,
        setIsMenuOpen: PropTypes.func,
        onMenuToggle: PropTypes.func,
    }),
};

export default VehiculeViewPage; 